import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { InvestmentService } from '../../services/investment.service';
import { ROUTE_PATHS } from '../../utils/route-paths.const';

/**
 * Guards /investment.
 * - Blocks browser Back/Forward (`trigger === 'popstate'`) into a flow that
 *   was just completed — the user would otherwise land back on a step
 *   (e.g. checkout) whose state was already cleared, with no way to
 *   submit again. Redirects to HOME instead. A genuine new entry (clicking
 *   "Invest Now" again, an 'imperative' navigation) clears this flag.
 * - Blocks entry when `state: { canInvest: false }` is explicitly present
 *   (e.g. the blocked screen has a link back to the investment flow — should
 *   not be allowed). The user stays on their current URL.
 * - All other navigations are allowed through; the investment component itself
 *   calls validateEntry() and redirects to /investment-blocked if needed.
 * - A cold direct-URL visit with canInvest=false falls back to HOME.
 */
export const investmentGuard: CanActivateFn = (): boolean => {
  const router = inject(Router);
  const flowService = inject(InvestmentService);
  const nav = router.currentNavigation();

  if (nav?.trigger === 'popstate' && flowService.wasCompleted()) {
    router.navigate([ROUTE_PATHS.HOME], {
      replaceUrl: true,
      state: { bypassGuard: true },
    });
    return false;
  }
  if (nav?.trigger === 'imperative') {
    flowService.clearCompletedFlag();
  }

  if (nav?.extras.state?.['canInvest'] !== false) return true;

  const hasPriorNav = nav?.previousNavigation != null;
  if (hasPriorNav) {
    router.navigateByUrl(router.url, {
      replaceUrl: true,
      state: { bypassGuard: true },
    });
  } else {
    router.navigate([ROUTE_PATHS.HOME], {
      replaceUrl: true,
      state: { bypassGuard: true },
    });
  }
  return false;
};
